import { Button } from "@/components/ui/button";
import { ArrowRight, FileText, Zap, Shield, Gift, Users } from "lucide-react";
import edugenixLogo from "@/assets/edugenix-logo.png";
import { Link } from "react-router-dom";

const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center pt-20 pb-16 overflow-hidden hero-gradient">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-accent/10 rounded-full blur-3xl animate-float-delayed" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
      </div>

      {/* Grid Pattern Overlay */}
      <div 
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `linear-gradient(hsl(var(--foreground)) 1px, transparent 1px),
                           linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }}
      />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-8 animate-fade-up">
            <img src={edugenixLogo} alt="EduGenix logo" className="w-5 h-5 rounded" />
            <span className="text-sm font-medium text-primary">AI-Powered Academic Excellence</span>
          </div>

          {/* Headline */}
          <h1 className="font-heading text-4xl md:text-5xl lg:text-7xl font-bold leading-tight mb-6 animate-fade-up">
            Your Project,{" "}
            <span className="gradient-text">Ready in Seconds.</span>
          </h1>

          {/* Subheadline */}
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-6 leading-relaxed animate-fade-up-delayed">
            Generate complete, submission-ready academic projects in 10-20 seconds. 
            Professional formatting, accurate citations, zero editing required.
          </p>

          {/* Free Project Banner */}
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-success/10 border border-success/30 mb-8 animate-scale-in">
            <Gift className="w-5 h-5 text-success" />
            <span className="text-sm font-medium text-success">
              New users get 3 free project downloads — no credit card required!
            </span>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8 animate-fade-up-delayed">
            <Button variant="hero" size="xl" className="group hover:scale-105 transition-transform" asChild>
              <Link to="/auth">
                Start Creating Free
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
            <Button variant="heroOutline" size="xl" className="hover:scale-105 transition-transform" asChild>
              <Link to="/dashboard">
                Try Demo
              </Link>
            </Button>
          </div>

          {/* Trust Indicators */}
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mb-12 animate-fade-up-delayed">
            <Users className="w-4 h-4 text-primary" />
            <span>Trusted by over 10,000 students worldwide</span>
          </div>

          {/* Feature Pills */}
          <div className="flex flex-wrap items-center justify-center gap-4 animate-fade-up-delayed">
            {[
              { icon: Zap, label: "10-20 Second Generation" },
              { icon: FileText, label: "Word & PDF Export" },
              { icon: Shield, label: "Institution Ready" },
            ].map((feature, index) => (
              <div
                key={index}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/50 border border-border/50 hover:border-primary/30 transition-colors"
              >
                <feature.icon className="w-4 h-4 text-primary" />
                <span className="text-sm text-muted-foreground">{feature.label}</span>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default Hero;
